// M09 - EducaFileDetailDrawer
//
// Drawer lateral para editar metadata del fichero (nombre, descripcion,
// visibilidad, carpeta) sin abandonar el grid. El blob en GCS NO se
// mueve aunque cambies la carpeta: el cambio es solo logico en Mongo.

import React, { useEffect, useState } from "react";
import {
  Drawer,
  Form,
  Input,
  Select,
  Button,
  Space,
  message,
  Popconfirm,
} from "antd";
import {
  GlobalOutlined,
  LockOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
} from "@ant-design/icons";

const VIS_OPTIONS = [
  {
    value: "public",
    label: (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <GlobalOutlined /> Publica (URL externa)
      </span>
    ),
  },
  {
    value: "private",
    label: (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <LockOutlined /> Privada (requiere admin)
      </span>
    ),
  },
];

export function EducaFileDetailDrawer({
  open,
  file,
  folders,
  streamUrl,
  onClose,
  onSave,
  onDelete,
  onDownload,
}) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && file) {
      form.setFieldsValue({
        name: file.name,
        description: file.description,
        visibility: file.visibility,
        folder: file.folder || "",
      });
    }
  }, [open, file, form]);

  if (!file) return null;

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await onSave(file.id, values);
      message.success("Fichero actualizado");
      onClose();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.body?.message || e?.message || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = () => {
    try {
      navigator.clipboard.writeText(streamUrl);
      message.success("URL copiada");
    } catch (e) {
      message.error("No se pudo copiar");
    }
  };

  const folderOptions = [
    { value: "", label: "EducaFiles (raiz)" },
    ...folders.map((f) => ({ value: f.name, label: f.name })),
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Editar fichero"
      width={420}
      destroyOnClose
      extra={
        <Popconfirm
          title="Borrar fichero"
          description="Esta accion borra el fichero en GCS y en la base de datos."
          okText="Borrar"
          okButtonProps={{ danger: true }}
          cancelText="Cancelar"
          onConfirm={() => onDelete(file)}
        >
          <Button danger icon={<DeleteOutlined />}>
            Borrar
          </Button>
        </Popconfirm>
      }
      footer={
        <Space
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Button
            icon={<DownloadOutlined />}
            onClick={() => onDownload?.(file)}
          >
            Descargar
          </Button>
          <Space>
            <Button onClick={onClose}>Cancelar</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>
              Guardar
            </Button>
          </Space>
        </Space>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <div
          style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", marginBottom: 4 }}
        >
          Nombre original
        </div>
        <div style={{ fontSize: 14, color: "#2b323f", wordBreak: "break-all" }}>
          {file.originalName}
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <div
          style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", marginBottom: 4 }}
        >
          URL para compartir
        </div>
        <Input.Group compact>
          <Input
            value={streamUrl}
            readOnly
            style={{ width: "calc(100% - 36px)" }}
          />
          <Button icon={<CopyOutlined />} onClick={handleCopy} />
        </Input.Group>
      </div>

      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="name"
          label="Nombre visible"
          rules={[{ required: true, message: "Requerido" }]}
        >
          <Input maxLength={120} />
        </Form.Item>

        <Form.Item name="description" label="Descripcion">
          <Input.TextArea rows={3} maxLength={500} showCount />
        </Form.Item>

        <Form.Item
          name="visibility"
          label="Visibilidad"
          rules={[{ required: true }]}
        >
          <Select options={VIS_OPTIONS} />
        </Form.Item>

        <Form.Item name="folder" label="Carpeta">
          <Select options={folderOptions} />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
